/**
 * Reducer + initial-state builder for the AIChatPracticeSession slice.
 *
 * Reset semantics:
 *   - RESET_PRACTICE_SESSION zeroes completedActivitiesCount/activityResults/sessionKCs and
 *     nulls activityExtraInfo, but does NOT clear the identity fields (instance/name/id).
 *   - ADD_ACTIVITY_RESULT appends (immutable), UPDATE_SESSION_KCS fully replaces.
 */
import type { PracticeSessionAction, PracticeSessionState } from './aiChatPracticeSessionTypes';

export const PRACTICE_SESSION_ACTIONS = {
  SET_ACTIVITY_INSTANCE_ID: 'SET_ACTIVITY_INSTANCE_ID',
  SET_ACTIVITY_NAME: 'SET_ACTIVITY_NAME',
  SET_ACTIVITY_EXTRA_INFO: 'SET_ACTIVITY_EXTRA_INFO',
  SET_ACTIVITY_ID: 'SET_ACTIVITY_ID',
  SET_COMPLETED_ACTIVITIES_COUNT: 'SET_COMPLETED_ACTIVITIES_COUNT',
  ADD_ACTIVITY_RESULT: 'ADD_ACTIVITY_RESULT',
  UPDATE_SESSION_KCS: 'UPDATE_SESSION_KCS',
  RESET_PRACTICE_SESSION: 'RESET_PRACTICE_SESSION',
} as const;

/**
 * Build the initial PracticeSession state, applying any provided overrides on top of the
 * documented defaults. Overrides come from the PracticeSession slice of AIChatProvider's own
 * initialState prop (initial-state seeding).
 */
export function getInitialPracticeSessionState(overrides: Partial<PracticeSessionState> = {}): PracticeSessionState {
  return {
    activityInstanceId: null,
    activityName: '',
    activityExtraInfo: null,
    activityId: null,
    completedActivitiesCount: 0,
    activityResults: [],
    sessionKCs: [],
    ...overrides,
  };
}

export function practiceSessionReducer(state: PracticeSessionState, action: PracticeSessionAction): PracticeSessionState {
  switch (action.type) {
    case PRACTICE_SESSION_ACTIONS.SET_ACTIVITY_INSTANCE_ID:
      return { ...state, activityInstanceId: action.payload };
    case PRACTICE_SESSION_ACTIONS.SET_ACTIVITY_NAME:
      return { ...state, activityName: action.payload };
    case PRACTICE_SESSION_ACTIONS.SET_ACTIVITY_EXTRA_INFO:
      return { ...state, activityExtraInfo: action.payload };
    case PRACTICE_SESSION_ACTIONS.SET_ACTIVITY_ID:
      return { ...state, activityId: action.payload };
    case PRACTICE_SESSION_ACTIONS.SET_COMPLETED_ACTIVITIES_COUNT:
      return { ...state, completedActivitiesCount: action.payload };
    case PRACTICE_SESSION_ACTIONS.ADD_ACTIVITY_RESULT:
      return { ...state, activityResults: [...state.activityResults, action.payload] };
    case PRACTICE_SESSION_ACTIONS.UPDATE_SESSION_KCS:
      return { ...state, sessionKCs: action.payload };
    case PRACTICE_SESSION_ACTIONS.RESET_PRACTICE_SESSION:
      return {
        ...state,
        completedActivitiesCount: 0,
        activityResults: [],
        sessionKCs: [],
        activityExtraInfo: null,
      };
    default:
      return state;
  }
}
